import React from "react";
import { POS_COLORS } from "../data/players";
import { MOCK_ROSTER_SIZE } from "../data/archetypes";
import { BYE_WEEKS_2026 } from "../data/scheduleData";
import { DEFAULT_SETTINGS } from "../data/scoring";
import { buildRosterSlots } from "../engine/simulation";
import { fillRosterSlots } from "../utils/rosterSlotFill";
import theme from "../data/theme";

// rosterSlots is settings-derived (buildRosterSlots(settings.positions)) — its
// length is the same value useMockDraft uses to gate real bids. It drives both
// the Max/counter math and the expanded-roster slot rows, so the two can't drift
// apart. The DEFAULT_SETTINGS/MOCK_ROSTER_SIZE fallbacks are for callers that
// omit the prop; both are stale for any non-16-man format.
const TeamGrid = React.memo(function TeamGrid({ teams, userSlot, currentBidder, nominatorIndex, mockPhase, expandedTeam, setExpandedTeam, draftLog, teamCount, rosterSlots, startingBudget }) {
  const slotDefs = rosterSlots?.length ? rosterSlots : buildRosterSlots(DEFAULT_SETTINGS.positions);
  const rosterSize = slotDefs.length || MOCK_ROSTER_SIZE;
  // team.budget is what's LEFT, so spend math needs the league's starting
  // budget. SFLEX runs $240 — a $200 constant here under-reports spend and
  // overruns the bar.
  const budget = startingBudget || DEFAULT_SETTINGS.budget;
  return (
    <>
      {/* Desktop Scoreboard */}
      <div className="mock-scoreboard" style={{ marginBottom: expandedTeam !== null ? 0 : 16 }}>
        {Array.from({ length: teamCount }, (_, i) => {
          const team = teams[i];
          if (!team) return null;
          const isNominator = i === nominatorIndex;
          const isHighBidder = mockPhase === "bidding" && i === currentBidder;
          const isExpanded = expandedTeam === i;
          const pct = ((budget - team.budget) / budget) * 100;
          const emptySlots = rosterSize - team.roster.length;
          const maxBid = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);
          const isRosteredOut = emptySlots === 0;
          const isMaxDollar = !isRosteredOut && maxBid <= 1;
          return (
            <div key={i} className="mock-team-card" onClick={() => setExpandedTeam(prev => prev === i ? null : i)} style={{
              background: isExpanded ? theme.helpers.brandBg(0.1) : isHighBidder ? "rgba(34,197,94,0.08)" : isNominator ? theme.helpers.brandBg() : isRosteredOut ? "rgba(239,68,68,0.10)" : isMaxDollar ? "rgba(245,158,11,0.10)" : theme.colors.bg.surface,
              border: `1px solid ${isExpanded ? "rgba(255,107,0,0.4)" : isHighBidder ? "rgba(34,197,94,0.3)" : isNominator ? theme.colors.border.brand : theme.colors.border.subtle}`,
              borderRadius: 8, padding: "8px 10px", cursor: "pointer", transition: "all 0.15s ease",
            }}>
              <div className="mock-team-card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: team.isUser ? theme.colors.brand.orange : theme.colors.text.primary, textDecoration: isRosteredOut ? "line-through" : "none" }}>
                    {team.name || `Team ${i + 1}`}{team.isUser ? " (You)" : ""}
                  </span>
                  {!team.isUser && <div className="mock-team-archetype" style={{ fontSize: 8, color: theme.colors.text.secondary, marginTop: 1 }}>{team.profile?.archetype}</div>}
                </div>
                <span style={{ fontSize: 10, color: theme.colors.text.secondary }}>{team.roster.length}/{rosterSize}</span>
              </div>
              {isRosteredOut ? (
                <div style={{ fontSize: 11, fontWeight: 800, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.5px" }}>ROSTER FULL</div>
              ) : isMaxDollar ? (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: team.budget < 20 ? theme.colors.status.danger : theme.colors.extended.emerald }}>${team.budget}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#f59e0b" }}>⚠ Max: $1</span>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: team.budget < 20 ? theme.colors.status.danger : theme.colors.extended.emerald }}>${team.budget}</span>
                  <span style={{ fontSize: 10, color: theme.colors.text.secondary }}>Max: <span style={{ fontWeight: 700, color: theme.colors.brand.accent }}>${maxBid}</span></span>
                </div>
              )}
              <div className="mock-team-card-bar" style={{ background: theme.colors.border.subtle, borderRadius: 3, height: 3, marginTop: 4 }}>
                <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3,
                  background: pct > 80 ? theme.colors.status.danger : theme.colors.brand.orange,
                  transition: "width 0.3s ease" }} />
              </div>
            </div>
          );
        })}
      </div>
      {/* Mobile Scoreboard Chips */}
      <div className="mock-scoreboard-mobile" style={{ display: "none", gridTemplateColumns: "repeat(5, 1fr)", gap: 3 }}>
        {Array.from({ length: teamCount }, (_, i) => {
          const team = teams[i];
          if (!team) return null;
          const isExpanded = expandedTeam === i;
          const isNominator = i === nominatorIndex;
          const isHighBidder = mockPhase === "bidding" && i === currentBidder;
          const emptySlots = rosterSize - team.roster.length;
          const maxBid = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);
          const isRosteredOut = emptySlots === 0;
          const isMaxDollar = !isRosteredOut && maxBid <= 1;
          return (
            <div key={i} onClick={() => setExpandedTeam(prev => prev === i ? null : i)} style={{
              padding: "4px 2px", borderRadius: 8, cursor: "pointer",
              textAlign: "center", overflow: "hidden",
              background: team.isUser ? theme.helpers.brandBg(0.12) : isExpanded ? theme.helpers.brandBg(0.15) : isHighBidder ? "rgba(34,197,94,0.1)" : isNominator ? theme.helpers.brandBg(0.1) : isRosteredOut ? "rgba(239,68,68,0.10)" : isMaxDollar ? "rgba(245,158,11,0.10)" : theme.colors.bg.surface,
              border: `1px solid ${team.isUser ? "rgba(255,107,0,0.5)" : isExpanded ? "rgba(255,107,0,0.4)" : isHighBidder ? "rgba(34,197,94,0.3)" : isNominator ? theme.colors.border.brand : "rgba(255,255,255,0.08)"}`,
              color: team.isUser ? theme.colors.brand.orange : theme.colors.text.secondary,
              transition: "all 0.15s ease",
            }}>
              <div style={{ fontSize: 8, fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", textDecoration: isRosteredOut ? "line-through" : "none" }}>{team.name || `Team ${i + 1}`}</div>
              {isRosteredOut ? (
                <div style={{ fontSize: 9, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.2 }}>FULL</div>
              ) : (
                <>
                  <div style={{ fontSize: 11, fontWeight: 800, color: team.budget < 20 ? theme.colors.status.danger : team.isUser ? theme.colors.brand.orange : theme.colors.extended.emerald, lineHeight: 1.2 }}>
                    ${team.budget}
                  </div>
                  {isMaxDollar ? (
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#f59e0b", lineHeight: 1.2 }}>⚠ Max: $1</div>
                  ) : emptySlots > 1 && (
                    <div style={{ fontSize: 10, color: theme.colors.brand.accent, lineHeight: 1.2 }}>Max: ${maxBid}</div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      {/* Expanded Team Roster */}
      {expandedTeam !== null && teams[expandedTeam] && (() => {
        const team = teams[expandedTeam];
        const filled = fillRosterSlots(team.roster, slotDefs);

        return (
          <div className="mock-expanded-roster" style={{
            background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.brand}`, borderRadius: "0 0 10px 10px",
            borderTop: "none", padding: "10px 14px", marginBottom: 16,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: team.isUser ? theme.colors.brand.orange : theme.colors.text.primary }}>
                {(team.name || `Team ${expandedTeam + 1}`)}'s Roster ({team.roster.length}/{rosterSize})
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, color: theme.colors.text.secondary }}>${budget - team.budget} spent</span>
                <button
                  onClick={() => setExpandedTeam(null)}
                  aria-label="Close roster"
                  style={{
                    background: theme.colors.border.subtle, border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: theme.radius.md, color: theme.colors.text.muted, fontSize: theme.fontSize.heading,
                    cursor: "pointer", width: 22, height: 22, display: "flex", alignItems: "center",
                    justifyContent: "center", fontFamily: theme.fonts.display, lineHeight: 1, flexShrink: 0,
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {slotDefs.map(slot => {
                const pick = filled[slot.id];
                const pickLog = pick ? draftLog.find(l => l.player.name === pick.player.name) : null;
                const slotPos = slot.pos === "BN" ? "BN" : slot.pos === "FLEX" ? "FLEX" : slot.pos === "SFLEX" ? "SFLEX" : pick?.player.pos || slot.pos;
                return (
                  <div key={slot.id} style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "3px 0",
                    opacity: pick ? 1 : 0.4,
                  }}>
                    <span className="tag" style={{
                      background: POS_COLORS[slotPos]?.bg || theme.colors.bg.elevated,
                      color: POS_COLORS[slotPos]?.text || theme.colors.text.muted,
                      fontSize: 8, padding: "1px 5px", minWidth: 36, textAlign: "center", fontWeight: 700,
                    }}>
                      {slot.label}
                    </span>
                    {pick ? (
                      <>
                        <span style={{ flex: 1, fontSize: 11, fontWeight: 500, color: theme.colors.text.primary }}>{pick.player.name}</span>
                        {BYE_WEEKS_2026[pick.player.team] && <span style={{ fontSize: 9, color: theme.colors.text.muted }} title="Bye week">B{BYE_WEEKS_2026[pick.player.team]}</span>}
                        {pickLog && <span style={{ fontSize: 9, color: theme.colors.text.muted }}>#{pickLog.pickNumber}</span>}
                        <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.brand.accent }}>${pick.price}</span>
                      </>
                    ) : (
                      <span style={{ flex: 1, fontSize: 11, color: theme.colors.border.default, fontStyle: "italic" }}>Empty</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </>
  );
});

export default TeamGrid;
